// TODO(#69): Technical debt
// Currently, PubNub response values are not strictly typed.
// To improve type safety and maintainability, PubNub type definitions should be organized and addressed in the future.
import React, { useCallback, useEffect, useState } from 'react';
import { Loading } from '../Loading';
import { Text } from '../Text';
import InfiniteScroll from 'react-infinite-scroll-component';
import { TChannel } from '../../api/messages';
import { ChannelListItem } from './ChannelListItem';
import { usePubNub } from 'pubnub-react';
import { MessageEvent } from 'pubnub';
import { commonStrings } from '../../constants/strings';
import { isIncomingChannelNotification } from '../../utils/chatRecallMessage';
import {
  CHAT_LIST_HISTORY_FETCH_COUNT,
  buildChatListHistoryPage,
  buildChatListHydrationKey,
  findLatestVisibleChatListMessageWithPagination,
  isChatListMessageVisible,
  mergeHydratedChatListMessages,
  mergeLatestChatListMessages,
  retainVisibleChatListMessages,
} from '../../utils/chatListMessage';

type Props = {
  isLoading?: boolean;
  channels: TChannel[];
  fetchMore: () => void;
  hasMore: boolean;
  onSelectChannel: (id: string) => void;
  currentChannel?: string | null;
  errorMsg?: string;
  refetch: () => void;
  lastMessageList?: {
    [x: string]: {
      channel: string;
      message: any;
      timetoken: string | number;
      uuid?: string;
    };
  };
  unreadMessageList?: {
    [x: string]: number;
  };
  getUnreadCount?: (item: TChannel & { lastMessage?: any }) => number;
  // InfiniteScroll の next 再解禁に使う「取得済み件数」。重複排除後の channels.length は
  // ページ境界で重複が起きると増えず無限スクロールが止まるため、重複排除前の件数を渡す。
  // 未指定時は従来どおり channels.length にフォールバックする。
  dataLength?: number;
};

export function ChannelList({
  isLoading,
  channels,
  fetchMore,
  hasMore,
  onSelectChannel,
  currentChannel,
  errorMsg,
  refetch: _refetch,
  lastMessageList: propLastMessageList,
  unreadMessageList: propUnreadMessageList,
  getUnreadCount: propGetUnreadCount,
  dataLength,
}: Props) {
  const pubnub = usePubNub();

  // Use props if provided, otherwise use local state (for backward compatibility)
  const [localLastMessageList, setLocalLastMessageList] = useState<{
    [x: string]: {
      channel: string;
      message: any;
      timetoken: string | number;
      uuid?: string;
    };
  }>({});
  const [localUnreadMessageList, setLocalUnreadMessageList] = useState<{
    [x: string]: number;
  }>({});
  const localLastMessageListRef = React.useRef(localLastMessageList);
  localLastMessageListRef.current = localLastMessageList;
  const channelsRef = React.useRef(channels);
  channelsRef.current = channels;
  // 一覧の再取得でunreadCountや並び順だけが変わっても全channelの履歴を取り直さない
  // よう、hydrateの再実行はchannel集合とtrust set（管理participant UUID）の変化に限る。
  const channelHydrationKey = React.useMemo(
    () => buildChatListHydrationKey(channels),
    [channels],
  );

  const lastMessageList = propLastMessageList || localLastMessageList;
  const unreadMessageList = propUnreadMessageList || localUnreadMessageList;

  const handleSelected = React.useCallback(
    (id: string) => {
      onSelectChannel(id);
      if (!propUnreadMessageList) {
        setLocalUnreadMessageList((prev) => ({
          ...prev,
          [id]: 0,
        }));
      }
    },
    [onSelectChannel, propUnreadMessageList]
  );

  // Only use local handlers if props are not provided
  const updateUnread = React.useCallback(
    (channelId: string, reset: boolean) => {
      if (!propUnreadMessageList) {
        setLocalUnreadMessageList((prev) => ({
          ...prev,
          [channelId]: reset
            ? 0
            : (prev[channelId] ?? 0) + 1,
        }));
      }
    },
    [propUnreadMessageList]
  );

  const handleMessage = React.useCallback(
    (event: MessageEvent) => {
      if (!propLastMessageList) {
        if (!isIncomingChannelNotification(event.message)) return;

        const channelId = event.message.channel;
        const currentUuid = pubnub?.getUUID();
        const isMyMessage = currentUuid === event.publisher;
        const managementParticipantUuids =
          channels.find((channel) => channel.id === channelId)
            ?.managementParticipantUuids ?? [];
        if (
          !isChatListMessageVisible(
            {
              message: event.message,
              uuid: event.publisher,
            },
            managementParticipantUuids,
          )
        ) {
          return;
        }

        setLocalLastMessageList((prev) =>
          mergeLatestChatListMessages(prev, {
            [channelId]: {
              channel: channelId,
              message: event.message,
              timetoken: event.timetoken,
              uuid: event.publisher,
            },
          }),
        );

        updateUnread(channelId, isMyMessage);
      }
    },
    [updateUnread, pubnub, propLastMessageList, channels]
  );

  // Fetch last messages for all channels on mount (only if props not provided)
  useEffect(() => {
    // 依存キーが変わったrenderのchannelsを使う（同一集合ならeffect自体が走らない）。
    const currentChannels = channelsRef.current;
    if (propLastMessageList || !pubnub || !currentChannels.length) return;
    let cancelled = false;
    // trust更新はnetwork成否を待たず、過去にfail-openしたmarkerへ即時反映する。
    setLocalLastMessageList((prev) =>
      retainVisibleChatListMessages(prev, currentChannels),
    );

    const fetchLastMessages = async () => {
      const previewsAtRequestStart = localLastMessageListRef.current;
      try {
        const res = await pubnub.fetchMessages({
          channels: currentChannels.map(({ id }) => id),
          count: CHAT_LIST_HISTORY_FETCH_COUNT,
          includeUUID: true,
          stringifiedTimeToken: true,
        });

        const channelResults = await Promise.allSettled(
          currentChannels.map(async (channel) => {
            const lastMsg =
              await findLatestVisibleChatListMessageWithPagination({
                initialPage: {
                  records: res.channels[channel.id] ?? [],
                },
                managementParticipantUuids:
                  channel.managementParticipantUuids,
                fetchOlder: async (start) => {
                  if (cancelled) return { records: [] };
                  const older = await pubnub.fetchMessages({
                    channels: [channel.id],
                    count: CHAT_LIST_HISTORY_FETCH_COUNT,
                    includeUUID: true,
                    stringifiedTimeToken: true,
                    start,
                  });
                  return buildChatListHistoryPage(
                    older.channels[channel.id] ?? [],
                    older.more,
                  );
                },
              });
            if (!lastMsg) return null;

            return [
              channel.id,
              {
                channel: channel.id,
                message: lastMsg.message,
                timetoken: lastMsg.timetoken,
                uuid: lastMsg.uuid,
              },
            ] as const;
          })
        );

        const newState: typeof localLastMessageList = {};
        const emptyChannelIds: string[] = [];
        channelResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            if (result.value) {
              const [channelId, message] = result.value;
              newState[channelId] = message;
            } else {
              emptyChannelIds.push(currentChannels[index].id);
            }
            return;
          }
          if (!cancelled) {
            console.error(
              `Error fetching last message for channel ${currentChannels[index].id}:`,
              result.reason,
            );
          }
        });

        if (!cancelled) {
          setLocalLastMessageList((prev) => {
            const visiblePrev = retainVisibleChatListMessages(
              prev,
              currentChannels,
            );
            return mergeHydratedChatListMessages({
              current: visiblePrev,
              candidates: newState,
              emptyChannelIds,
              previewsAtRequestStart,
            });
          });
        }
      } catch (err) {
        if (!cancelled) console.error('Error fetching last messages:', err);
      }
    };

    void fetchLastMessages();
    return () => {
      cancelled = true;
    };
  }, [pubnub, channelHydrationKey, propLastMessageList]);

  // Only add listener if props not provided (subscribe is done at page level)
  useEffect(() => {
    if (propLastMessageList || !pubnub) return;

    const listenerParams = {
      message: handleMessage,
    };

    pubnub.addListener(listenerParams);

    return () => {
      pubnub.removeListener(listenerParams);
    };
  }, [handleMessage, pubnub, propLastMessageList]);

  // Page-level callers already provide sorted channels. The compatibility
  // fallback still has to expose its locally hydrated/realtime preview.
  const channelsView = React.useMemo(
    () =>
      channels.map((channel) => {
        const channelWithLastMessage = channel as TChannel & {
          lastMessage?: {
            message: unknown;
            timetoken: string | number;
            uuid?: string;
          };
        };
        const lastMessage =
          lastMessageList[channel.id] || channelWithLastMessage.lastMessage;
        return {
          ...channel,
          // effect cleanup前のrenderでも、最新trustでhiddenなら描画しない。
          lastMessage:
            lastMessage &&
            isChatListMessageVisible(
              lastMessage,
              channel.managementParticipantUuids,
            )
              ? lastMessage
              : undefined,
        };
      }),
    [channels, lastMessageList],
  );

  const getUnreadCount = useCallback(
    (item: TChannel & { lastMessage?: any }) => {
      if (propGetUnreadCount) {
        return propGetUnreadCount(item);
      }
      // Fallback: If last message is from current user, unread count should be 0
      const currentUuid = pubnub?.getUUID();
      const lastMessage = lastMessageList[item.id] || item.lastMessage;
      if (lastMessage?.uuid === currentUuid) {
        return 0;
      }
      return unreadMessageList[item.id] ?? item.unreadCount ?? 0;
    },
    [unreadMessageList, lastMessageList, pubnub, propGetUnreadCount]
  );

  return (
    <>
      {isLoading ? (
        <div className="my-10 text-center">
          <Loading />
        </div>
      ) : !channels.length || errorMsg ? (
        <Text className="mt-6 tracking-widest" center bold>
          {errorMsg || commonStrings.noData}
        </Text>
      ) : (
        <div className="overflow-y-auto h-full" id="scrollableChannel">
          <InfiniteScroll
            next={fetchMore}
            hasMore={hasMore}
            loader={
              <div className="py-[36px] flex justify-center items-center">
                <Loading />
              </div>
            }
            dataLength={dataLength ?? channels.length}
            scrollThreshold={0.5}
            className="flex flex-col grow"
            scrollableTarget="scrollableChannel"
          >
            <div className="flex flex-col">
              {channelsView.map((item) => {
                return (
                  <React.Fragment key={item.id}>
                    <ChannelListItem
                      channel={item}
                      onSelectChannel={handleSelected}
                      isActive={currentChannel === item.id}
                      unreadCount={getUnreadCount(item)}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </InfiniteScroll>
        </div>
      )}
    </>
  );
}

export { ChannelListHeader } from './ChannelListHeader';
